import SoftBox from "components/SoftComponents/SoftBox";
import { useQuery } from '@tanstack/react-query';
import { getCameraImage, loadToken } from 'services/api/bks';
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { loadingScreen } from "utils/wrapper";
import { useSoftUIController } from "hooks/context";
import ParkingValidation from "assets/json/ParkingValidation.json";

function DetectionImage({ image_id, device, is_occupancy = true }) {
  loadToken(useAuth0());

  const [controller] = useSoftUIController();
  const { selectedOrg } = controller;

  const { isLoading, error, data, isFetching } = useQuery({
    queryKey: ['getCameraImage', image_id, selectedOrg],
    queryFn: async () => {
      const data = await getCameraImage(image_id);
      return data;
    },
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <SoftBox height="100%" my={5}>
        {loadingScreen(controller, false)}
      </SoftBox>
    )
  }

  if (error) {
    return "Error! " + error.message;
  }

  const blob = new Blob([data]);
  const imageUrl = URL.createObjectURL(blob);

  const { x1, y1, x2, y2 } = ParkingValidation["locations"][device.stop.site_id]["line"];

  return (
    <PhotoProvider id={blob}>
      <SoftBox sx={{ height: "100%" }}>
        <PhotoView src={imageUrl}>
          <SoftBox
            position="relative"
            display="flex"
            justifyContent="center"
            width="100%"
            pb={1}
            sx={{ lineHeight: 0 }}
          >
            <SoftBox
              display="grid"
              sx={{
                placeItems: 'center',
                width: 'fit-content',
                aspectRatio: '640 / 480',
                position: 'relative',
                lineHeight: 0
              }}
            >
              <SoftBox
                component="img"
                src={imageUrl}
                borderRadius="lg"
                sx={{
                  gridArea: '1 / 1',
                  maxWidth: '100%',
                  height: 'auto',
                  minHeight: '300px',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
              />
              {is_occupancy && (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    gridArea: '1 / 1',
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none'
                  }}
                >
                  <defs>
                    <pattern
                      id="hazardStripes"
                      patternUnits="userSpaceOnUse" // Keeps stripes consistent regardless of line length
                      width="4" // Total width of one black + one yellow stripe
                      height="4"
                      patternTransform="rotate(45)" // Standard hazard angle
                    >
                      <rect width="4" height="4" fill="#000000" />
                      <rect width="2" height="4" fill="#FFD700" />
                    </pattern>
                  </defs>
                  <line
                    x1={`${(x1 / 640) * 100}%`}
                    y1={`${(y1 / 480) * 100}%`}
                    x2={`${(x2 / 640) * 100}%`}
                    y2={`${(y2 / 480) * 100}%`}
                    stroke="url(#hazardStripes)"
                    strokeWidth="1.5"
                    strokeLinecap="butt"
                    opacity={0.5}
                  />
                </svg>
              )}
            </SoftBox>
          </SoftBox>
        </PhotoView>
      </SoftBox>
    </PhotoProvider>
  );
}

// Typechecking props for the DetectionImage
DetectionImage.propTypes = {
  image_id: PropTypes.string,
  device: PropTypes.object,
  is_occupancy: PropTypes.bool
};


export default DetectionImage;
